import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiCpu, FiMessageCircle, FiZap, FiTrendingUp, FiPhoneCall, FiCalendar } from 'react-icons/fi';

const AIFeaturesSection = () => {
  const { t } = useTranslation();

  const aiFeatures = [
    {
      icon: <FiZap className="w-10 h-10" />,
      titleKey: 'landing.aiFeatures.features.rpa.title',
      descriptionKey: 'landing.aiFeatures.features.rpa.description',
      color: 'from-purple-500 to-pink-500',
      examplesKey: 'landing.aiFeatures.features.rpa.examples'
    },
    {
      icon: <FiTrendingUp className="w-10 h-10" />,
      titleKey: 'landing.aiFeatures.features.approval.title',
      descriptionKey: 'landing.aiFeatures.features.approval.description',
      color: 'from-blue-500 to-cyan-500',
      examplesKey: 'landing.aiFeatures.features.approval.examples'
    },
    {
      icon: <FiMessageCircle className="w-10 h-10" />,
      titleKey: 'landing.aiFeatures.features.voice.title',
      descriptionKey: 'landing.aiFeatures.features.voice.description',
      color: 'from-green-500 to-teal-500',
      examplesKey: 'landing.aiFeatures.features.voice.examples'
    },
    {
      icon: <FiCpu className="w-10 h-10" />,
      titleKey: 'landing.aiFeatures.features.copilot.title',
      descriptionKey: 'landing.aiFeatures.features.copilot.description',
      color: 'from-orange-500 to-red-500',
      examplesKey: 'landing.aiFeatures.features.copilot.examples'
    },
    {
      icon: <FiPhoneCall className="w-10 h-10" />,
      titleKey: 'landing.aiFeatures.features.phone.title',
      descriptionKey: 'landing.aiFeatures.features.phone.description',
      color: 'from-violet-500 to-fuchsia-500',
      examplesKey: 'landing.aiFeatures.features.phone.examples'
    },
    {
      icon: <FiCalendar className="w-10 h-10" />,
      titleKey: 'landing.aiFeatures.features.reminders.title',
      descriptionKey: 'landing.aiFeatures.features.reminders.description',
      color: 'from-emerald-500 to-lime-500',
      examplesKey: 'landing.aiFeatures.features.reminders.examples'
    }
  ];

  return (
    <section id="ai-features" className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white" aria-label={t('landing.aiFeaturesSection.ariaLabel')}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block bg-white/10 dark:bg-surface-900/10 backdrop-blur-sm px-6 py-2 rounded-full mb-4">
            <span className="text-blue-200 text-sm font-semibold">🤖 {t('landing.aiFeatures.badge')}</span>
          </div>
          <h2 className="text-4xl font-extrabold mb-4" data-testid="ai-features-title">
            {t('landing.aiFeatures.title')}
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto" data-testid="ai-features-subtitle">
            {t('landing.aiFeatures.subtitle')}
            <br />
            <span className="text-sm text-blue-200 mt-2 block">{t('landing.aiFeatures.subtitleExtra')}</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {aiFeatures.map((feature, index) => (
            <article
              key={index}
              className="bg-white/10 dark:bg-surface-900/10 backdrop-blur-lg rounded-2xl p-8 hover:bg-white/15 transition duration-300 border border-white/20"
              data-testid={`ai-feature-card-${index}`}
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} mb-6`}>
                {feature.icon}
              </div>
              <h3 className="text-2xl font-bold mb-4">{t(feature.titleKey)}</h3>
              <p className="text-blue-100 mb-6 leading-relaxed">{t(feature.descriptionKey)}</p>
              <div className="space-y-2">
                <p className="text-sm text-blue-200 font-semibold mb-3">{t('landing.aiFeatures.examples')}</p>
                {t(feature.examplesKey, { returnObjects: true }).map((example, i) => (
                  <div key={i} className="flex items-center text-sm text-blue-100">
                    <span className="me-2">✓</span>
                    <span>{example}</span>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>

        {/* AI Vision Statement */}
        <div className="bg-gradient-to-r from-purple-600/50 to-pink-600/50 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold mb-4">🎯 {t('landing.aiFeatures.vision.title')}</h3>
            <p className="text-xl text-blue-100 mb-6 leading-relaxed">
              {t('landing.aiFeatures.vision.description')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
              <div className="bg-white/10 dark:bg-surface-900/10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold text-white mb-2">{t('landing.aiFeaturesSection.statManualReduction')}</div>
                <div className="text-sm text-blue-200">{t('landing.aiFeatures.vision.stats.manualReduction')}</div>
              </div>
              <div className="bg-white/10 dark:bg-surface-900/10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold text-white mb-2">10x</div>
                <div className="text-sm text-blue-200">{t('landing.aiFeatures.vision.stats.fastDecision')}</div>
              </div>
              <div className="bg-white/10 dark:bg-surface-900/10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold text-white mb-2">{t('landing.aiFeaturesSection.statAlwaysOn')}</div>
                <div className="text-sm text-blue-200">{t('landing.aiFeatures.vision.stats.aiAssistant')}</div>
              </div>
              <div className="bg-white/10 dark:bg-surface-900/10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold text-white mb-2">0</div>
                <div className="text-sm text-blue-200">{t('landing.aiFeatures.vision.stats.manualError')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIFeaturesSection;
